import {
    AddressValidationError,
    InvalidStateError,
    InvalidZipError,
    InvalidZipSubRegionError,
    MissingFieldError,
    isStateFieldRequired,
} from '@/utils/address'

import { AddressInput, getCountryData, getCountryFields } from './types'

/**
 * @description Validates an address object and throws a MissingFieldsError if any required fields are missing
 * @param address The address object to validate
 * @throws MissingFieldsError if any required fields are missing
 * @throws CountryMissingError if the country data is not found
 */
export const validateAddress = (address: AddressInput) => {
    const data = getCountryData(address.country)
    const fields = getCountryFields(address.country)
    const errors: ConstructorParameters<typeof AddressValidationError>[0] = []

    // we only validate the state when set or when the country requires it
    const stateFieldRequired = address.state || isStateFieldRequired(address.country)

    Object.entries(fields)
        .filter(([field]) => field !== 'state' || stateFieldRequired)
        .forEach(([field, rule]) => {
            const value = address[field as keyof AddressInput]?.trim()

            if (rule === 'required' && !value) {
                errors.push(new MissingFieldError(field))
            }
        })

    if (data.zip) {
        const regex = new RegExp(`^${data.zip}$`)
        const subRegion = address.state ? data.sub_regions?.find((sr) => sr.key === address.state) : null
        const zip = address.zip?.trim() ?? ''

        if (!regex.test(zip)) {
            errors.push(new InvalidZipError(regex.source))
        }

        if (subRegion?.zip) {
            const subRegionRegex = new RegExp(`^${subRegion.zip}`)

            if (!subRegionRegex.test(zip)) {
                errors.push(new InvalidZipSubRegionError(regex.source, subRegionRegex.source))
            }
        }
    }

    if (stateFieldRequired && fields.state === 'required') {
        if (!data.sub_regions?.find((sr) => sr.key === address.state)) {
            errors.push(new InvalidStateError(address.country))
        }
    }

    if (errors.length) {
        throw new AddressValidationError(errors)
    }
}
